import React from 'react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import {
  Plus, X, AlertTriangle, Clock, Calendar, Send,
  Edit, Trash2, Wrench, Bell, BarChart3, History, Shield
} from 'lucide-react';

export const SettingsDialog = ({
  open, onOpenChange, tab, setTab, maintenanceTypes, alertConfig, setAlertConfig,
  alertDashboard, alertHistory, newRecipient, setNewRecipient, onSaveAlerts, onSendAlerts,
  sendingAlerts, loadAlerts, showTypeForm, setShowTypeForm, editType, setEditType,
  typeForm, setTypeForm, onSaveType, onDeleteType, hasRole
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="text-base">Setari Preventiva</DialogTitle></DialogHeader>
      <div className="flex gap-1 mb-4 border-b pb-2 overflow-x-auto">
        {[
          { key: 'types', label: 'Tipuri', icon: Wrench },
          { key: 'alerts', label: 'Alerte', icon: Bell },
          { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
          { key: 'history', label: 'Istoric', icon: History },
        ].map(t => (
          <Button key={t.key} size="sm" variant={tab === t.key ? 'default' : 'ghost'} className="text-xs h-8 gap-1 flex-shrink-0"
            onClick={() => { setTab(t.key); if (t.key !== 'types') loadAlerts(); }}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </Button>
        ))}
      </div>

      {tab === 'types' && (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium">Nomenclator tipuri intretinere</span>
            <Button size="sm" className="h-7 text-xs" onClick={() => { setEditType(null); setTypeForm({ name: '', description: '', operatiuni: '' }); setShowTypeForm(true); }} data-testid="add-type-btn">
              <Plus className="w-3 h-3 mr-1" /> Adauga
            </Button>
          </div>
          {maintenanceTypes.map(t => (
            <div key={t.id} className="border rounded-lg p-3">
              <div className="flex items-start justify-between">
                <div>
                  <span className="font-medium text-sm">{t.name}</span>
                  {t.description && <p className="text-xs text-slate-500">{t.description}</p>}
                  {t.operatiuni && <p className="text-xs text-slate-400 mt-1 line-clamp-2">{t.operatiuni}</p>}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditType(t); setTypeForm({ name: t.name, description: t.description || '', operatiuni: t.operatiuni || '' }); setShowTypeForm(true); }}>
                    <Edit className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => onDeleteType(t.id)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
          {showTypeForm && (
            <div className="border-2 border-blue-200 rounded-lg p-3 space-y-2 bg-blue-50/50">
              <Input className="h-9 text-sm" value={typeForm.name} onChange={e => setTypeForm({...typeForm, name: e.target.value})} placeholder="Denumire tip *" data-testid="type-name-input" />
              <Input className="h-9 text-sm" value={typeForm.description} onChange={e => setTypeForm({...typeForm, description: e.target.value})} placeholder="Descriere" />
              <Textarea className="text-sm" rows={2} value={typeForm.operatiuni} onChange={e => setTypeForm({...typeForm, operatiuni: e.target.value})} placeholder="Operatiuni realizate la intretinere..." />
              <div className="flex gap-2">
                <Button size="sm" onClick={onSaveType} disabled={!typeForm.name} data-testid="save-type-btn">{editType ? 'Actualizeaza' : 'Salveaza'}</Button>
                <Button size="sm" variant="ghost" onClick={() => setShowTypeForm(false)}>Anuleaza</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'alerts' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              <div><span className="text-sm font-semibold">Sistem alerte</span><p className="text-xs text-slate-500">Activare/dezactivare</p></div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={alertConfig.enabled} onChange={e => setAlertConfig({...alertConfig, enabled: e.target.checked})} className="sr-only peer" />
              <div className="w-9 h-5 bg-slate-200 rounded-full peer peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-500">Zile inainte sf. luna</Label>
              <Input type="number" className="h-9 text-sm" value={alertConfig.days_before_month_end} onChange={e => setAlertConfig({...alertConfig, days_before_month_end: parseInt(e.target.value)||0})} /></div>
            <div><Label className="text-xs text-slate-500">Zile dupa sf. luna</Label>
              <Input type="number" className="h-9 text-sm" value={alertConfig.days_after_month_end} onChange={e => setAlertConfig({...alertConfig, days_after_month_end: parseInt(e.target.value)||0})} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-500">Ora trimitere</Label>
              <Select value={String(alertConfig.schedule_hour||8)} onValueChange={v => setAlertConfig({...alertConfig, schedule_hour: parseInt(v)})}>
                <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{[6,7,8,9,10,11,12,13,14,15,16,17,18].map(h => <SelectItem key={h} value={String(h)}>{h}:00</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-slate-500">Zile</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {['luni','marti','miercuri','joi','vineri'].map(d => {
                  const a = (alertConfig.schedule_days||[]).includes(d);
                  return <button key={d} onClick={() => setAlertConfig({...alertConfig, schedule_days: a ? (alertConfig.schedule_days||[]).filter(x=>x!==d) : [...(alertConfig.schedule_days||[]), d]})}
                    className={`px-2 py-1 rounded text-xs font-medium ${a ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`}>{d.slice(0,3)}</button>;
                })}
              </div>
            </div>
          </div>
          <div><Label className="text-xs font-semibold text-slate-700 mb-1.5 block">Destinatari ({(alertConfig.recipients||[]).length})</Label>
            {(alertConfig.recipients||[]).map((r,i) => (
              <div key={i} className="flex items-center gap-2 bg-slate-50 rounded px-3 py-1.5 mb-1">
                <span className="text-sm flex-1">{r.name} <span className="text-slate-400 text-xs">{r.email}</span></span>
                <button className="text-red-400 hover:text-red-600" onClick={() => setAlertConfig({...alertConfig, recipients: alertConfig.recipients.filter((_,idx)=>idx!==i)})}><X className="w-3 h-3" /></button>
              </div>
            ))}
            <div className="flex gap-1.5 mt-1.5">
              <Input placeholder="Nume" className="h-8 text-xs flex-1" value={newRecipient.name} onChange={e => setNewRecipient({...newRecipient, name: e.target.value})} />
              <Input placeholder="Email" className="h-8 text-xs flex-1" value={newRecipient.email} onChange={e => setNewRecipient({...newRecipient, email: e.target.value})} />
              <Button size="sm" variant="outline" className="h-8 px-3" onClick={() => { if (newRecipient.name && newRecipient.email) { setAlertConfig({...alertConfig, recipients: [...(alertConfig.recipients||[]), {...newRecipient}]}); setNewRecipient({name:'',email:''}); } }}><Plus className="w-3 h-3" /></Button>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button className="flex-1" onClick={onSaveAlerts} data-testid="save-alerts-btn">Salveaza</Button>
            <Button variant="outline" className="gap-1" onClick={onSendAlerts} disabled={sendingAlerts} data-testid="send-alerts-btn">
              <Send className="w-3.5 h-3.5" />{sendingAlerts ? 'Se trimit...' : 'Trimite acum'}
            </Button>
          </div>
        </div>
      )}

      {tab === 'dashboard' && alertDashboard && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Restante', value: alertDashboard.overdue_count, bg: 'bg-red-50', text: 'text-red-700', icon: AlertTriangle },
              { label: alertDashboard.current_month_name, value: alertDashboard.pending_current_month, bg: 'bg-amber-50', text: 'text-amber-700', icon: Clock },
              { label: 'Zile ramase', value: alertDashboard.days_left_in_month, bg: 'bg-blue-50', text: 'text-blue-700', icon: Calendar },
              { label: 'Alerte trimise', value: alertDashboard.total_alerts_sent, bg: 'bg-emerald-50', text: 'text-emerald-700', icon: Send },
            ].map((s,i) => (
              <div key={i} className={`rounded-lg border p-3 ${s.bg}`}>
                <div className="flex items-center gap-1.5 mb-1"><s.icon className={`w-3.5 h-3.5 ${s.text}`} /><span className="text-xs text-slate-500">{s.label}</span></div>
                <span className={`text-xl font-bold ${s.text}`}>{s.value}</span>
              </div>
            ))}
          </div>
          {alertDashboard.overdue_by_section?.length > 0 && (
            <div>
              <Label className="text-xs font-semibold mb-2 block">Restante per sectie</Label>
              {alertDashboard.overdue_by_section.map((s,i) => (
                <div key={i} className="flex items-center justify-between bg-slate-50 rounded px-3 py-2 mb-1">
                  <span className="text-sm">{s.section}</span>
                  <Badge className="bg-red-100 text-red-700">{s.count}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'history' && (
        <div className="space-y-2">
          {alertHistory.length === 0 ? <p className="text-center py-8 text-sm text-slate-400">Nicio alerta trimisa</p> :
            alertHistory.map((h,i) => (
              <div key={i} className="border rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <Badge className={h.status==='success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>{h.status==='success' ? 'Trimis' : 'Eroare'}</Badge>
                  <span className="text-xs text-slate-500">{h.type==='manual' ? 'Manual' : 'Automat'}</span>
                  <span className="text-xs text-slate-400 ml-auto">{h.sent_at ? new Date(h.sent_at).toLocaleString('ro-RO') : ''}</span>
                </div>
                <p className="text-xs text-slate-600 mt-1">{h.recipients_count} dest. · {h.overdue_count} restante · {h.pending_count} in asteptare</p>
              </div>
            ))
          }
        </div>
      )}
    </DialogContent>
  </Dialog>
);
